import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from 'react-i18next';

// Inline Icons to avoid lucide-react dependency issues
const HeartIcon = ({ size = 16, fill = "none", className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
    </svg>
);

const BabyIcon = ({ size = 18, className = "" }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M9 12h.01" />
        <path d="M15 12h.01" />
        <path d="M10 16c.5.3 1.2.5 2 .5s1.5-.2 2-.5" />
        <path d="M19 6.3a9 9 0 0 1 1.8 3.9 2 2 0 0 1 0 3.6 9 9 0 0 1-17.6 0 2 2 0 0 1 0-3.6A9 9 0 0 1 12 3c2 0 3.5 1.1 5.3 2.3Z" />
        <path d="M12 3v2" />
    </svg>
);

const CharacterVote = ({ characterId }) => {
    const { t } = useTranslation();
    const { isAuthenticated } = useAuth();
    const [votes, setVotes] = useState({ wife: 0, daughter: 0 });
    const [myVote, setMyVote] = useState(null); // 'Wife' | 'Daughter' | null
    const [loading, setLoading] = useState(true);
    const [lastVotedDate, setLastVotedDate] = useState(null);
    const [notification, setNotification] = useState(null); // Local notification state

    // Initial Fetch (Safe for guests, GET IS protected but we expect 200 for public or 401 fail)
    useEffect(() => {
        if (!characterId) return;

        const fetchVotes = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_BASE_URL}/api/character/${characterId}/vote`);
                if (response.ok) {
                    const data = await response.json();
                    setVotes({
                        wife: data.stats.Wife || 0,
                        daughter: data.stats.Daughter || 0
                    });
                    setMyVote(data.my_vote);
                    if (data.my_vote && data.last_voted_at) {
                        setLastVotedDate(new Date(data.last_voted_at).toLocaleDateString());
                    } else if (data.my_vote) {
                        setLastVotedDate(new Date().toLocaleDateString());
                    }
                }
            } catch (error) {
                console.error("Error loading votes:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchVotes();
    }, [characterId]);

    const handleVote = async (voteType) => {
        // 1. Guest Check
        if (!isAuthenticated) {
            setNotification(t('CharacterVote.notification_guest'));
            setTimeout(() => setNotification(null), 3000);
            return;
        }

        // Prevent voting if already loading or if clicking expected vote
        if (loading) return;
        if (myVote === voteType) return;

        const previousVote = myVote;
        const previousVotes = { ...votes };

        // Optimistic Update
        const newVotes = { ...votes };

        // Decrement old vote if exists
        if (previousVote) {
            const prevKey = previousVote.toLowerCase();
            newVotes[prevKey] = Math.max(0, newVotes[prevKey] - 1);
        }

        // Increment new vote
        const newKey = voteType.toLowerCase();
        newVotes[newKey] = (newVotes[newKey] || 0) + 1;

        setVotes(newVotes);
        setMyVote(voteType);
        setLastVotedDate(new Date().toLocaleDateString());

        try {
            const response = await fetch(`${API_BASE_URL}/api/character/${characterId}/vote`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ vote: voteType })
            });

            if (!response.ok) {
                throw new Error('Failed to submit vote');
            }
        } catch (error) {
            console.error("Vote Error:", error);
            // Rollback
            setVotes(previousVotes);
            setMyVote(previousVote);
        }
    };

    const totalVotes = votes.wife + votes.daughter;
    const wifePercentage = totalVotes === 0 ? 50 : (votes.wife / totalVotes) * 100;
    const daughterPercentage = totalVotes === 0 ? 50 : (votes.daughter / totalVotes) * 100;

    // Limits for display to avoid overlap
    const displayWife = Math.round(wifePercentage);
    const displayDaughter = Math.round(daughterPercentage);

    return (
        <div className="mt-2 pt-6 border-t border-gray-100 relative">
            {/* Guest Notification Banner */}
            {notification && (
                <div className="absolute top-0 left-0 right-0 -mt-8 flex justify-center z-50 animate-fade-in-down transition-all duration-300">
                    <div className="bg-gray-800 text-white text-xs font-bold py-1.5 px-4 rounded-full shadow-lg flex items-center gap-2">
                        <svg className="w-4 h-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                        {notification}
                    </div>
                </div>
            )}

            <div className="flex justify-between items-end mb-2">
                <span className="text-xs font-bold text-rose-500 uppercase tracking-widest block">Wife</span>
                <span className="text-xs font-bold text-blue-500 uppercase tracking-widest block">Daughter</span>
            </div>

            {/* Barra de Progreso Unificada */}
            <div className="relative w-full h-8 bg-blue-400 rounded-full overflow-hidden flex shadow-inner font-black text-sm text-white leading-none">

                {/* Capa Rosa (Wife) */}
                <div
                    className="absolute top-0 left-0 h-full bg-rose-400 transition-all duration-500 ease-out z-10 flex items-center justify-start px-3"
                    style={{ width: `${wifePercentage}%` }}
                >
                    {/* Texto Wife - Mostrar solo si hay espacio */}
                    {(wifePercentage > 15 || totalVotes === 0) && (
                        <span className="animate-in fade-in zoom-in whitespace-nowrap z-20 relative">{displayWife}%</span>
                    )}

                    {/* El Divisor Diagonal */}
                    <div className="absolute top-0 right-0 bottom-0 w-4 bg-white transform skew-x-[-20deg] translate-x-1/2 h-full z-10 shadow-[0_0_2px_rgba(0,0,0,0.1)]"></div>
                </div>

                {/* Texto Daughter */}
                <div className="absolute top-0 right-0 h-full flex items-center justify-end px-3 z-0 w-full">
                    {(daughterPercentage > 15 || totalVotes === 0) && (
                        <span className="animate-in fade-in zoom-in">{displayDaughter}%</span>
                    )}
                </div>
            </div>

            {/* Botones + Fecha */}
            <div className="flex items-center justify-between mt-5">
                <button
                    onClick={() => handleVote('Wife')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 border ${myVote === 'Wife'
                        ? 'bg-rose-500 text-white border-rose-600 shadow-md ring-2 ring-rose-200'
                        : 'bg-rose-50 hover:bg-rose-100 text-rose-600 border-rose-100'
                        }`}
                >
                    <HeartIcon size={16} fill={myVote === 'Wife' ? "currentColor" : "none"} /> Vote Wife
                </button>

                <div className="flex flex-col items-center">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">{t('CharacterVote.label_last_voted')}</span>
                    <span className="text-xs font-semibold text-gray-600">{lastVotedDate || t('CharacterVote.label_never')}</span>
                </div>

                <button
                    onClick={() => handleVote('Daughter')}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 border ${myVote === 'Daughter'
                        ? 'bg-blue-500 text-white border-blue-600 shadow-md ring-2 ring-blue-200'
                        : 'bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-100'
                        }`}
                >
                    Vote Daughter <BabyIcon size={18} />
                </button>
            </div>
        </div>
    );
};

export default CharacterVote;
